import React from 'react'

import type { CommonDeps } from '../base/CommonDeps'
import { debounceTimeout } from './debounceTimeout'
import { renderCatch } from '../error/renderCatch'
import { strictParseFloat } from '../../../shared/lib/number.no-deps'
import { ExecutionElement } from '../flow/ExecutionElement'
import { ElementLabel } from './ElementLabel'

import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import type { CreateAnswer } from '../../../shared/domain/flows/answer.no-deps'
import type { TimelineDetails } from './TimelineDetails'
import type { LoopPathIds } from '../../../shared/domain/flows/LoopPath.no-deps'
import { isMobile } from '../../../shared/lib/isMobile'
import type { CamelCaseFlags } from '../../../shared/cfg/flags.no-deps'

interface Props {
    deps: CommonDeps
    elementId: Flows.ElementId
    loopPathIds: LoopPathIds
    label: string
    timelineDetails: TimelineDetails
    required: boolean
    unitOfMeasurement: string
    answerValue: Flows.NumberAnswer['value']
    validationMessages: string[]
    createAnswer: CreateAnswer<Flows.NumberAnswer>
    openOptions?: () => void
    featureFlags: Pick<CamelCaseFlags, 'useNewElementNumberInput'>
}

type InputMode = React.HTMLAttributes<HTMLInputElement>['inputMode']

export const ElementNumber = (props: Props) => {
    const timeout = React.useRef<ReturnType<typeof setTimeout>>(null)
    const [editValue, setEditValue] = React.useState<string | null>(null)

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Set the value in local state as a string
        setEditValue(event.target.value)

        // Create answer with debounce
        clearTimeout(timeout.current)
        timeout.current = window.setTimeout(() => createAnswer(event.target), debounceTimeout)
    }

    const onFocus = () => setEditValue(props.answerValue?.toString() ?? '')

    const onBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
        clearTimeout(timeout.current)
        await createAnswer(event.target)
        setEditValue(null)
    }

    // Prevent number updates when using scroll wheel.
    const onWheelCapture = (evt: React.WheelEvent<HTMLInputElement>) => {
        // If the new number input is enabled, the wheel does nothing
        if (props.featureFlags.useNewElementNumberInput) {
            return
        }

        // Otherwise, prevent the wheel from changing the input
        evt.currentTarget.blur()
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // If the new number input is not enabled, we can ignore this function
        if (!props.featureFlags.useNewElementNumberInput) {
            return
        }

        const allowedKeys = [
            'Backspace',
            'Tab',
            'Enter',
            'Escape',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
        ]

        // Allow navigation, Ctrl/Cmd+A/C/V/X/Z/Y, etc
        if (event.ctrlKey || event.metaKey || allowedKeys.includes(event.key)) {
            return
        }

        // Allow digits
        if (event.key >= '0' && event.key <= '9') {
            return
        }

        // Allow a minus sign only if it's the first character and is not already present
        if (
            event.key === '-' &&
            event.currentTarget.selectionStart === 0 &&
            event.currentTarget.value.includes('-') === false
        ) {
            return
        }

        // Allow a comma or dot only if neither are already present
        if (
            (event.key === ',' || event.key === '.') &&
            event.currentTarget.value.includes(',') === false &&
            event.currentTarget.value.includes('.') === false
        ) {
            return
        }

        event.preventDefault()
    }

    const createAnswer = async (element: HTMLInputElement) => {
        // Parse the string to a number
        // Users in a non-English locale may enter a comma instead of a dot
        // e.g. 1,56 instead of 1.56, so we normalise to a dot before parsing
        const value = element.value.replace(',', '.')
        const valueAsNumber = strictParseFloat(value)

        // Ignore if the number value hasn't changed
        if (props.answerValue === valueAsNumber) {
            return
        }

        // Otherwise create an answer
        await props.createAnswer((base) => ({
            ...base,
            type: 'number',
            value: valueAsNumber,
        }))
    }

    let inputMode: InputMode
    let inputType: string

    if (props.featureFlags.useNewElementNumberInput) {
        // In the new mode, we use "decimal" input mode, except for iOS and Samsung
        inputMode = isMobile.Samsung() || isMobile.iOS() ? 'text' : 'decimal'
        inputType = 'text'
    } else {
        // Fix for Samsung devices not displaying the "minus" symbol in the keypad.
        ;[inputType, inputMode] = isMobile.Samsung() ? ['text', 'numeric'] : ['number', 'text']
    }

    const value = editValue ?? props.answerValue?.toString() ?? ''

    return renderCatch(props.deps, () => (
        <ExecutionElement
            deps={props.deps}
            elementId={props.elementId}
            loopPathIds={props.loopPathIds}
            validationMessages={props.validationMessages}
            timelineDetails={props.timelineDetails}
        >
            <ElementLabel
                required={props.required}
                label={props.label}
                hasBeenEdited={props.timelineDetails?.hasBeenEdited}
                language={props.deps.language}
                validationMessages={props.validationMessages}
                openOptions={props.openOptions}
            />
            <div className="AppCore__flow__ElementNumber">
                <div className="AppCore__flow__ElementNumber__inputWrapper">
                    <input
                        type={inputType}
                        className="AppCore__flow__ElementNumber__input focusable"
                        value={value}
                        inputMode={inputMode}
                        onChange={onChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onWheelCapture={onWheelCapture}
                        onKeyDown={onKeyDown}
                        tabIndex={-1}
                        maxLength={15}
                    />
                    {props.unitOfMeasurement && (
                        <div className="AppCore__flow__ElementNumber__unit">{props.unitOfMeasurement}</div>
                    )}
                </div>
            </div>
        </ExecutionElement>
    ))
}
